import { useModals } from 'almanac-ui/Modal'
import { useRootStore } from 'stores/RootStore'
import { Button } from 'blaze-ui/Button'
import { ButtonSize } from 'blaze-ui/Button/Types'
import { Icon } from 'almanac-ui/icons/Types'
import { Plus } from 'blaze-ui/icons/20'
import { Shimmerer } from 'blaze-ui/Shimmerer'
import { Wizards } from 'components/Wizards/Wizards'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { WizardsDrawer } from 'components/Wizards/WizardsDrawer/WizardsDrawer'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { observer } from 'mobx-react-lite'
import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { useDispatch } from 'react-redux'
import { setFloatingSidebar } from 'redux/actions'
import { useRouteMatch } from 'react-router-dom'
import { DOC_PATH, FOLDER_DOC_PATH } from 'paths'
import { useRecentWizards } from 'hooks/useRecentWizards'
import { SHOW_RECENT_WIZARDS_MAX } from 'components/Wizards/WizardsV3/WizardsV3'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { useWizardDrafts } from 'hooks/queries/wizardDrafts/useWizardDrafts'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { Tone } from 'components/VoiceAndTonesSelect/Types'
import { useMemo } from 'react'
import { useOpenAutopilotUpgrade } from 'hooks/useOpenAutopilotUpgrade'
import { NavMenuItem } from '../NavMenuItem/NavMenuItem'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { CreateContentModal } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/CreateContentModal/CreateContentModal'
import { AddMoreContentModal } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/AddMoreContentModal/AddMoreContentModal'

type Props = {
  variant?: ButtonVariant
  size?: ButtonSize
  frontIcon?: Icon
  endIcon?: Icon
  label?: string
  withShimmer?: boolean
  ariaLabel?: string
  onBack?: () => void
  isNavMenuItem?: boolean
  onPress?: () => void
  onSuccess?: (postsCount: number) => void
  customTrigger?: React.ReactNode
  openWizardsModal?: boolean
}

export const BlazeNewButton = observer(
  ({
    variant,
    size = 'md',
    frontIcon = Plus,
    endIcon,
    label = 'Create New',
    withShimmer = true,
    onBack,
    ariaLabel,
    isNavMenuItem = false,
    onPress,
    onSuccess,
    customTrigger,
    openWizardsModal = false,
  }: Props) => {
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const { openModal } = useModals()
    const { timerStore, drawersStore, leftSidebarStore } = useRootStore()
    const { isMobile } = useResponsiveDesign()
    const currentWorkspace = useCurrentWorkspace()
    const openVirtualMarketerPlansModal = useOpenAutopilotUpgrade()
    const isCreateContentModalFlagEnabled = useFlipperFlag('create_content_modal_v2')
    const { data: brandPlan } = useCurrentBrandPlan()
    const useNewContentModal = !isMobile && isCreateContentModalFlagEnabled && !!brandPlan?.has_content_strategies

    const { data: brandKits } = useBrandKits()

    const initialValues = useMemo(() => {
      const latestBrandKit = brandKits?.[brandKits.length - 1]

      const brandKitFields = {
        brand_kit_id: latestBrandKit?.id,
        brand_voice_id: latestBrandKit?.brand_voice?.id,
        brand_visual_kit_id: latestBrandKit?.brand_visual_kit?.id,
        tone: latestBrandKit?.brand_voice?.voice_params?.tone as Tone[] | undefined,
      }

      return {
        ...brandKitFields,
        brand_voice_id: latestBrandKit?.brand_voice?.id,
      }
    }, [brandKits])

    const dispatch = useDispatch()
    const onFolderDocPath = useRouteMatch(FOLDER_DOC_PATH)
    const onDocPath = useRouteMatch(DOC_PATH)
    const onDoc = onFolderDocPath?.isExact || onDocPath?.isExact

    //prefetch data for create new modal
    useRecentWizards({ count: SHOW_RECENT_WIZARDS_MAX })
    useWizardDrafts()

    const guardedOpenAddMoreContentModal = usePaywallGuard(
      () => {
        const ContentModal = useNewContentModal ? CreateContentModal : AddMoreContentModal
        openModal(ContentModal, { allowWizardsModal: true, onSuccess })
      },
      { allowFreemium: true },
    )

    const handlePress = () => {
      if (onDoc) {
        leftSidebarStore.setIsHovered(false)
        UserAppPreferencesService.unlockSidebar()
        dispatch(setFloatingSidebar(false))
      }

      if (currentWorkspace?.is_ap_freemium) {
        openVirtualMarketerPlansModal()
        return
      }

      if (isSystemRedesignEnabled && !openWizardsModal) {
        guardedOpenAddMoreContentModal()
        return
      }

      timerStore.startTimer()
      openModal(Wizards, { initialValues, onBack })
      onPress?.()
    }

    const handleMobilePress = () => {
      if (currentWorkspace?.is_ap_freemium) {
        openVirtualMarketerPlansModal()
        return
      }
      drawersStore.openDrawer(WizardsDrawer, { initialValues })
      onPress?.()
    }

    if (customTrigger) {
      return <div onClick={isMobile ? handleMobilePress : handlePress}>{customTrigger}</div>
    }

    if (isNavMenuItem) {
      return <NavMenuItem size="md" icon={Plus} activeIcon={Plus} onPress={handlePress} label="Create New" />
    }

    if (isMobile)
      return (
        <Button
          fullWidth
          variant={variant}
          size="md"
          frontIcon={Plus}
          aria-label={ariaLabel}
          onPress={() => {
            drawersStore.openDrawer(WizardsDrawer, { initialValues })
          }}
        >
          Create New
        </Button>
      )

    if (withShimmer) {
      return (
        <Shimmerer overrideChildBackgroundOnHover>
          <Button
            fullWidth
            variant={variant}
            size={size}
            frontIcon={endIcon ? undefined : frontIcon}
            endIcon={endIcon}
            onPress={handlePress}
            aria-label={ariaLabel}
          >
            {label}
          </Button>
        </Shimmerer>
      )
    }

    return (
      <Button
        variant={variant}
        size={size}
        frontIcon={endIcon ? undefined : frontIcon}
        endIcon={endIcon}
        onPress={handlePress}
        aria-label={ariaLabel}
      >
        {label}
      </Button>
    )
  },
)
